import { useState, useEffect } from 'react';
import { useUser } from '@clerk/clerk-react';
// import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { ExternalLink, Building2, MapPin, Globe, Mail, Users, Briefcase, Pencil, Check, AlertCircle, Sparkles, Loader2 } from 'lucide-react';
import { useOwnClientProfile } from '@/lib/hooks/use-own-client-profile';
import { useRole } from '@/hooks/use-role';
import { useClientProfileCompletion } from '@/lib/hooks/use-client-profile-completion';
import { ProfilePictureUploadGeneric } from '@/components/profile/profile-picture-upload-generic';
import { CompleteProfileBanner } from '@/components/profile/complete-profile-banner';
import { SlideIn, FadeIn } from '@/components/motion';
import { cn } from '@/lib/utils';
import { useAIWritingAssistant } from '@/lib/hooks/use-ai-writing-assistant';
import { AIAssistantDialog } from '@/components/ai/ai-assistant-dialog';
import { emailService } from '@/lib/api/services/email.service';

export default function ClientProfile() {
  const { user } = useUser();
  const { clientProfileId } = useRole();
  const { clientProfile, saveClientProfile, isLoading } = useOwnClientProfile();
  const completion = useClientProfileCompletion(clientProfile);
  const { generateClientBio, isLoading: isAIGenerating, generatingType, data: aiGeneratedData, reset: resetAIData } = useAIWritingAssistant();

  // Form state - initialize from client profile or defaults
  const [companyName, setCompanyName] = useState('');
  const [companySize, setCompanySize] = useState<string | undefined>(undefined);
  const [industry, setIndustry] = useState('');
  const [location, setLocation] = useState('');
  const [bio, setBio] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [email, setEmail] = useState('');

  // Editing states for sections
  const [isEditingBasicInfo, setIsEditingBasicInfo] = useState(false);
  const [isEditingBio, setIsEditingBio] = useState(false);
  const [isEditingDetails, setIsEditingDetails] = useState(false);
  const [isAIDialogOpen, setIsAIDialogOpen] = useState(false);

  // Loading states for save operations
  const [isSavingBasicInfo, setIsSavingBasicInfo] = useState(false);
  const [isSavingBio, setIsSavingBio] = useState(false);
  const [isSavingDetails, setIsSavingDetails] = useState(false);

  // Validation errors for company details
  const [detailsErrors, setDetailsErrors] = useState<{
    companySize?: string;
    location?: string;
    industry?: string;
    websiteUrl?: string;
  }>({});

  // Temp states for editing
  const [tempCompanyName, setTempCompanyName] = useState('');
  const [tempEmail, setTempEmail] = useState('');
  const [tempBio, setTempBio] = useState('');
  const [tempCompanySize, setTempCompanySize] = useState<string | undefined>(undefined);
  const [tempIndustry, setTempIndustry] = useState('');
  const [tempLocation, setTempLocation] = useState('');
  const [tempWebsiteUrl, setTempWebsiteUrl] = useState('');

  // Helper function to map database company_size to UI value
  const mapDbCompanySizeToUI = (dbCompanySize: string | null | undefined): string | undefined => {
    const reverseCompanySizeMap: Record<string, string> = {
      '1-10': 'startup',
      '11-50': 'small',
      '51-200': 'medium',
      '201-500': 'enterprise',
      '501-1000': 'enterprise',
      '1000+': 'enterprise',
    };

    if (dbCompanySize && reverseCompanySizeMap[dbCompanySize]) {
      return reverseCompanySizeMap[dbCompanySize];
    }
    return undefined;
  };

  // Helper to get company size display label
  const getCompanySizeLabel = (size: string | undefined): string => {
    const labels: Record<string, string> = {
      'startup': 'Startup (1-10)',
      'small': 'Small (11-50)',
      'medium': 'Medium (51-200)',
      'enterprise': 'Enterprise (200+)',
    };
    return size ? labels[size] || size : '';
  };

  // Sync local state with client profile data when it changes
  useEffect(() => {
    if (clientProfile) {
      setCompanyName(clientProfile.company_name || '');
      const mappedValue = mapDbCompanySizeToUI(clientProfile.company_size);
      setCompanySize(mappedValue);
      setIndustry(clientProfile.industry || '');
      setLocation(clientProfile.location || '');
      setBio(clientProfile.bio || '');
      setWebsiteUrl(clientProfile.website_url || '');
      setEmail(clientProfile.email || '');
    } else if (!isLoading) {
      setCompanyName('');
      setCompanySize(undefined);
      setIndustry('');
      setLocation('');
      setBio('');
      setWebsiteUrl('');
      setEmail('');
    }
  }, [clientProfile, clientProfile?.company_size, isLoading]);

  // Auto-enter edit mode if bio was empty when dialog opens
  useEffect(() => {
    if (isAIDialogOpen && !bio && !isEditingBio) {
      setIsEditingBio(true);
      setTempBio(bio);
    }
  }, [isAIDialogOpen, bio, isEditingBio]);

  // Send email when profile reaches 100% completion (only once)
  useEffect(() => {
    if (!user?.id || !completion.isComplete || !clientProfile?.email) return;
    
    const storageKey = `client-profile-completion-email-sent-${user.id}`;
    const hasSentEmail = localStorage.getItem(storageKey) === 'true';
    
    if (!hasSentEmail) {
      // Mark as sent immediately to prevent duplicate sends
      localStorage.setItem(storageKey, 'true');
      
      // Send completion email (non-blocking)
      emailService.sendClientProfileCompletedEmail(clientProfile.email).catch((error) => {
        console.error('Failed to send profile completion email:', error);
        // Remove the flag on error so we can retry
        localStorage.removeItem(storageKey);
      });
    }
  }, [completion.isComplete, clientProfile?.email, user?.id]);

  // Map UI company_size values to database constraint values
  const companySizeMap: Record<string, string> = {
    'startup': '1-10',
    'small': '11-50',
    'medium': '51-200',
    'enterprise': '1000+',
  };

  // URL validation function - stricter validation for actual URLs
  const isValidUrl = (url: string): boolean => {
    if (!url.trim()) return true; // Empty is valid (optional field)
    
    const trimmedUrl = url.trim();
    
    // Try to create a URL object to ensure it's actually valid
    try {
      let normalizedUrl = trimmedUrl;
      if (!normalizedUrl.match(/^https?:\/\//i)) {
        normalizedUrl = `https://${normalizedUrl}`;
      }
      const urlObj = new URL(normalizedUrl);
      const hostname = urlObj.hostname;
      
      if (!hostname || hostname.length === 0) {
        return false;
      }
      
      // Special case for localhost
      if (hostname === 'localhost' || hostname === '127.0.0.1') {
        return true;
      }
      
      // Must contain at least one dot
      if (!hostname.includes('.')) {
        return false;
      }
      
      // Cannot start or end with a dot
      if (hostname.startsWith('.') || hostname.endsWith('.')) {
        return false;
      }
      
      // Must have a valid TLD (top-level domain)
      const parts = hostname.split('.');
      if (parts.length < 2) {
        return false;
      }
      
      // Get the last part (TLD)
      const tld = parts[parts.length - 1];
      
      // TLD must be at least 2 characters and max 63 characters
      if (!tld || tld.length < 2 || tld.length > 63 || !/^[a-z0-9-]+$/i.test(tld)) {
        return false;
      }
      
      // Prevent cases like "www.google" where "google" is mistaken for a TLD
      // If domain starts with "www", it must have at least 3 parts: "www.domain.tld"
      if (parts.length === 2 && parts[0] === 'www') {
        return false;
      }
      
      // Common TLDs are 2-4 characters (.com, .org, .net, .info, etc.)
      // Longer TLDs exist (.museum, .photography, etc.) but are less common
      // For 2-part domains (e.g., "domain.tld"), if TLD is > 5 chars, likely invalid
      // This catches cases where a word without TLD is mistaken for a valid domain
      if (parts.length === 2 && tld.length > 5) {
        return false;
      }
      
      return true;
    } catch {
      return false;
    }
  };

  // Save handlers for each section
  const saveBasicInfo = async () => {
    if (!tempCompanyName.trim()) {
      toast.error('Company name is required');
      return;
    }
    
    if (!saveClientProfile) return;

    setIsSavingBasicInfo(true);
    try {
      await saveClientProfile({
        company_name: tempCompanyName.trim(),
        email: email.trim() || null,
      });
      setCompanyName(tempCompanyName.trim());
      setIsEditingBasicInfo(false);
    } catch (_error) {
      // Error is already handled by the mutation's onError handler
      // Don't exit edit mode on error
    } finally {
      setIsSavingBasicInfo(false);
    }
  };

  const saveBio = async (directBio?: string) => {
    const bioToSave = directBio !== undefined ? directBio.trim() : tempBio.trim();
    
    if (!saveClientProfile) return;

    setIsSavingBio(true);
    try {
      await saveClientProfile({ bio: bioToSave || null });
      setBio(bioToSave);
      setIsEditingBio(false);
    } catch (_error) {
      // Error is already handled by the mutation's onError handler
      // Don't exit edit mode on error
    } finally {
      setIsSavingBio(false);
    }
  };

  const saveDetails = async () => {
    // Clear previous errors
    setDetailsErrors({});

    // Validate required fields
    const errors: {
      companySize?: string;
      location?: string;
      industry?: string;
      websiteUrl?: string;
    } = {};

    if (!tempCompanySize) {
      errors.companySize = 'Company size is required';
    }

    if (!tempLocation.trim()) {
      errors.location = 'Location is required';
    }

    if (!tempIndustry.trim()) {
      errors.industry = 'Industry is required';
    }

    // Validate URL if provided
    if (tempWebsiteUrl.trim() && !isValidUrl(tempWebsiteUrl.trim())) {
      errors.websiteUrl = 'Please enter a valid URL (e.g., www.example.com or https://example.com)';
    }

    // If there are validation errors, show them and don't save
    if (Object.keys(errors).length > 0) {
      setDetailsErrors(errors);
      toast.error('Please fix the errors before saving');
      return;
    }

    // Normalize website URL
    let normalizedWebsiteUrl = tempWebsiteUrl.trim();
    if (normalizedWebsiteUrl && !normalizedWebsiteUrl.match(/^https?:\/\//i)) {
      normalizedWebsiteUrl = `https://${normalizedWebsiteUrl}`;
    }

    const companySizeValue = tempCompanySize && companySizeMap[tempCompanySize]
      ? companySizeMap[tempCompanySize]
      : null;

    if (!saveClientProfile) return;

    setIsSavingDetails(true);
    try {
      await saveClientProfile({
        company_size: companySizeValue,
        industry: tempIndustry.trim() || null,
        location: tempLocation.trim() || null,
        website_url: normalizedWebsiteUrl || null,
      });
      setCompanySize(tempCompanySize);
      setIndustry(tempIndustry.trim());
      setLocation(tempLocation.trim());
      setWebsiteUrl(normalizedWebsiteUrl);
      setIsEditingDetails(false);
      setDetailsErrors({}); // Clear errors on success
    } catch (_error) {
      // Error is already handled by the mutation's onError handler
      // Don't exit edit mode on error
    } finally {
      setIsSavingDetails(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header Section */}
      <div
        className="py-8 sm:py-10 lg:py-12 px-4 sm:px-6 lg:px-8"
        style={{
          background: 'linear-gradient(180deg, rgba(232, 240, 254, 1) 0%, rgba(240, 245, 255, 1) 50%, rgba(248, 250, 252, 1) 100%)',
        }}
      >
        <div className="max-w-[1400px] mx-auto">
          {/* <FadeIn>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 sm:mb-8">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Building2 className="h-5 w-5 sm:h-6 sm:w-6 text-[#4669ed]" />
                  <span className="text-[#4669ed] font-semibold text-sm sm:text-base">Business Profile</span>
                </div>
              </div>
              {clientProfileId && (
                <Link
                  to={`/client/${clientProfileId}`}
                  className="flex items-center justify-center gap-2 px-4 py-2 rounded-full bg-white border-2 border-slate-200 text-slate-700 font-semibold text-sm hover:border-slate-300 transition-colors w-full sm:w-auto"
                >
                  <ExternalLink className="h-4 w-4" />
                  View Public Profile
                </Link>
              )}
            </div>
          </FadeIn> */}

          <SlideIn direction="up">
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-slate-900 mb-3 sm:mb-4">
              Your Business Profile
            </h1>
          </SlideIn>
          <FadeIn delay={0.1}>
            <p className="text-base sm:text-lg text-slate-500">
              Complete your profile to help AI experts understand your company and needs
            </p>
          </FadeIn>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 -mt-4 pb-12 sm:pb-16">
        {/* Complete Profile Banner */}
        {!isLoading && !completion.isComplete && (
          <FadeIn delay={0.2}>
            <CompleteProfileBanner
              percentage={completion.percentage}
              message={completion.nextIncompleteItem ? `Next step: ${completion.nextIncompleteItem.description}` : undefined}
            />
          </FadeIn>
        )}

        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
          {/* Main Content Area */}
          <div className="flex-1 order-2 lg:order-1 space-y-6">

            {/* Profile Picture & Basic Info Card */}
            <SlideIn direction="up" delay={0.2}>
              <div
                className="rounded-2xl sm:rounded-3xl lg:rounded-[32px] border border-slate-200 p-5 sm:p-7 lg:p-10"
                style={{
                  background: 'linear-gradient(158.39deg, rgba(255, 255, 255, 1) 0%, rgba(248, 250, 255, 1) 100%)',
                }}
              >
                <div className="flex flex-col sm:flex-row gap-6 sm:gap-8 items-center sm:items-start">
                  {/* Avatar */}
                  <div className="flex-shrink-0">
                    <ProfilePictureUploadGeneric
                      profileType="client"
                      profileId={clientProfileId}
                      currentUrl={clientProfile?.profile_picture_url || null}
                      name={clientProfile?.company_name || 'Company Logo'}
                    />
                  </div>

                  {/* Basic Info */}
                  <div className="flex-1 w-full">
                    {isEditingBasicInfo ? (
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-semibold text-slate-700 mb-2">
                            Company Name *
                          </label>
                          <Input
                            value={tempCompanyName}
                            onChange={(e) => setTempCompanyName(e.target.value)}
                            placeholder="Acme Corporation"
                            className="h-12 rounded-xl border-2 border-slate-200 focus:border-[#4669ed] focus:ring-0"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-slate-700 mb-2">
                            <Mail className="inline h-4 w-4 mr-1" />
                            Contact Email
                          </label>
                          <Input
                            type="email"
                            value={tempEmail}
                            readOnly
                            placeholder="contact@company.com"
                            className="h-12 rounded-xl border-2 border-slate-200 bg-slate-50 cursor-not-allowed"
                          />
                        </div>
                        <div className="flex gap-2 pt-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setTempCompanyName(companyName);
                              setTempEmail(email);
                              setIsEditingBasicInfo(false);
                            }}
                            disabled={isSavingBasicInfo}
                            className="flex-1 rounded-full"
                          >
                            Cancel
                          </Button>
                          <Button
                            size="sm"
                            onClick={saveBasicInfo}
                            disabled={isSavingBasicInfo}
                            className="flex-1 bg-[#4669ed] hover:bg-[#2b42a3] rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {isSavingBasicInfo ? (
                              <>
                                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                                Saving...
                              </>
                            ) : (
                              <>
                                <Check className="h-4 w-4 mr-1" />
                                Save
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900">
                              {companyName || 'Your Company Name'}
                            </h2>
                            {email && (
                              <p className="text-slate-500 mt-1 flex items-center gap-2">
                                <Mail className="h-4 w-4" />
                                {email}
                              </p>
                            )}
                          </div>
                          <button
                            onClick={() => {
                              setTempCompanyName(companyName);
                              setTempEmail(email);
                              setIsEditingBasicInfo(true);
                            }}
                            className="flex items-center justify-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3.5 rounded-[48px] border-2 border-slate-200 bg-white text-slate-900 font-normal text-sm sm:text-base hover:bg-slate-50 transition-colors"
                          >
                            <Pencil className="w-4 h-4 sm:w-5 sm:h-5" />
                            Edit
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </SlideIn>

            {/* About Your Business Card */}
            <SlideIn direction="up" delay={0.3}>
              <div
                className={cn(
                  "rounded-2xl sm:rounded-3xl lg:rounded-[32px] p-5 sm:p-7 lg:p-10",
                  !bio
                    ? "bg-amber-50/50 border-2 border-dashed border-amber-500/30"
                    : "bg-white border border-slate-200"
                )}
                style={bio ? {
                  background: 'linear-gradient(158.39deg, rgba(255, 255, 255, 1) 0%, rgba(248, 250, 255, 1) 100%)',
                } : undefined}
              >
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4 mb-4 sm:mb-6">
                  <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                    <Building2 className="h-5 w-5 sm:h-6 sm:w-6 text-slate-700" />
                    <h3 className="text-xl sm:text-2xl text-slate-900">About Your Business</h3>
                    {!bio && (
                      <span className="bg-amber-500 text-white rounded-[48px] px-3 sm:px-4 py-1 text-xs font-medium">
                        Incomplete
                      </span>
                    )}
                  </div>
                  {bio && !isEditingBio && (
                    <button
                      onClick={() => {
                        setTempBio(bio);
                        setIsEditingBio(true);
                      }}
                      className="flex items-center justify-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3.5 rounded-[48px] border-2 border-slate-200 bg-white text-slate-900 font-normal text-sm sm:text-base hover:bg-slate-50 transition-colors"
                    >
                      <Pencil className="w-4 h-4 sm:w-5 sm:h-5" />
                      Edit
                    </button>
                  )}
                </div>

                {isEditingBio || !bio ? (
                  (() => {
                    const currentBioLength = tempBio.length;
                    const minChars = 100;
                    const isValid = currentBioLength >= minChars;
                    return (
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <label className="block text-sm font-semibold text-slate-700">
                            Company Bio
                          </label>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => setIsAIDialogOpen(true)}
                            disabled={isAIGenerating}
                            className="h-7 px-2 text-xs text-[#4669ed] hover:text-[#2b42a3] hover:bg-[#4669ed]/10"
                          >
                            {isAIGenerating && generatingType === 'client_bio' ? (
                              <>
                                <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                                Generating...
                              </>
                            ) : (
                              <>
                                <Sparkles className="h-3 w-3 mr-1" />
                                Write company bio with AI
                              </>
                            )}
                          </Button>
                        </div>
                        <AIAssistantDialog
                          open={isAIDialogOpen}
                          onOpenChange={(open) => {
                            setIsAIDialogOpen(open);
                            if (!open) {
                              resetAIData();
                            }
                          }}
                          onGenerate={async (currentText, options) => {
                            return await generateClientBio(currentText ?? tempBio, options);
                          }}
                          onAccept={(text) => {
                            setTempBio(text);
                            resetAIData();
                          }}
                          generatedText={aiGeneratedData?.type === 'client_bio' ? aiGeneratedData.text : null}
                          isLoading={isAIGenerating && generatingType === 'client_bio'}
                          type="client_bio"
                          currentText={tempBio}
                        />
                        
                        <Textarea
                          value={tempBio}
                          onChange={(e) => setTempBio(e.target.value)}
                          placeholder="Tell AI experts about your company, mission, and what you're looking for..."
                          rows={5}
                          maxLength={1500}
                          className="rounded-xl border-2 border-slate-200 focus:border-[#4669ed] focus:ring-0 resize-none min-h-[120px]"
                        />
                        <div className="flex items-center justify-between">
                          <p className={cn(
                            "text-xs",
                            !isValid && currentBioLength > 0 ? "text-amber-500" : "text-slate-400"
                          )}>
                            {currentBioLength}/1500 characters
                            {!isValid && <span className="ml-2">(minimum {minChars} required)</span>}
                          </p>
                        </div>
                        <div className="flex gap-2 justify-end">
                          {isEditingBio && (
                            <Button 
                              variant="outline" 
                              onClick={() => {
                                setIsEditingBio(false);
                                setTempBio(bio);
                              }} 
                              disabled={isSavingBio}
                              className="rounded-[48px]"
                            >
                              Cancel
                            </Button>
                          )}
                          <Button
                            onClick={() => {
                              saveBio();
                            }}
                            disabled={!isValid || isSavingBio}
                            className="bg-[#4669ed] hover:bg-[#2b42a3] rounded-[48px] disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {isSavingBio ? (
                              <>
                                <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                                Saving...
                              </>
                            ) : (
                              <>
                                <Check className="h-5 w-5 mr-2" />
                                Save
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                    );
                  })()
                ) : (
                  <div className="text-slate-700 leading-relaxed whitespace-pre-wrap break-all min-w-0">
                    {bio}
                  </div>
                )}
              </div>
            </SlideIn>

            {/* Company Details Card */}
            <SlideIn direction="up" delay={0.4}>
              <div
                className={cn(
                  "rounded-2xl sm:rounded-3xl lg:rounded-[32px] p-5 sm:p-7 lg:p-10",
                  !industry || !companySize || !location
                    ? "bg-amber-50/50 border-2 border-dashed border-amber-500/30"
                    : "bg-white border border-slate-200"
                )}
                style={industry && companySize && location ? {
                  background: 'linear-gradient(158.39deg, rgba(255, 255, 255, 1) 0%, rgba(248, 250, 255, 1) 100%)',
                } : undefined}
              >
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4 mb-4 sm:mb-6">
                  <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                    <Briefcase className="h-5 w-5 sm:h-6 sm:w-6 text-slate-700" />
                    <h3 className="text-xl sm:text-2xl text-slate-900">Company Details</h3>
                    {(!industry || !companySize || !location) && (
                      <span className="bg-amber-500 text-white rounded-[48px] px-3 sm:px-4 py-1 text-xs font-medium">
                        Incomplete
                      </span>
                    )}
                  </div>
                  {!isEditingDetails && (
                    <button
                      onClick={() => {
                        setTempCompanySize(companySize);
                        setTempIndustry(industry);
                        setTempLocation(location);
                        setTempWebsiteUrl(websiteUrl);
                        setDetailsErrors({}); // Clear errors when entering edit mode
                        setIsEditingDetails(true);
                      }}
                      className="flex items-center justify-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3.5 rounded-[48px] border-2 border-slate-200 bg-white text-slate-900 font-normal text-sm sm:text-base hover:bg-slate-50 transition-colors"
                    >
                      <Pencil className="w-4 h-4 sm:w-5 sm:h-5" />
                      Edit
                    </button>
                  )}
                </div>

                {isEditingDetails ? (
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                          <Users className="inline h-4 w-4 mr-1" />
                          Company Size*
                        </label>
                        <Select
                          value={tempCompanySize || undefined}
                          onValueChange={(value) => {
                            setTempCompanySize(value);
                            if (detailsErrors.companySize) {
                              setDetailsErrors(prev => ({ ...prev, companySize: undefined }));
                            }
                          }}
                        >
                          <SelectTrigger className={cn(
                            "!h-12 w-full rounded-xl border-2 focus:ring-0 bg-white",
                            detailsErrors.companySize
                              ? "border-red-500 focus:border-red-500"
                              : "border-slate-200 focus:border-[#4669ed]"
                          )}>
                            <SelectValue placeholder="Select company size" />
                          </SelectTrigger>
                          <SelectContent className="rounded-xl border-2 border-slate-200">
                            <SelectItem value="startup" className="focus:bg-slate-200 focus:text-slate-900">Startup (1-10 employees)</SelectItem>
                            <SelectItem value="small" className="focus:bg-slate-200 focus:text-slate-900">Small (11-50 employees)</SelectItem>
                            <SelectItem value="medium" className="focus:bg-slate-200 focus:text-slate-900">Medium (51-200 employees)</SelectItem>
                            <SelectItem value="enterprise" className="focus:bg-slate-200 focus:text-slate-900">Enterprise (200+ employees)</SelectItem>
                          </SelectContent>
                        </Select>
                        {detailsErrors.companySize && (
                          <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                            <AlertCircle className="h-3 w-3" />
                            {detailsErrors.companySize}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                          <MapPin className="inline h-4 w-4 mr-1" />
                          Location*
                        </label>
                        <Input
                          value={tempLocation}
                          onChange={(e) => {
                            setTempLocation(e.target.value);
                            if (detailsErrors.location) {
                              setDetailsErrors(prev => ({ ...prev, location: undefined }));
                            }
                          }}
                          placeholder="San Francisco, CA"
                          className={cn(
                            "h-12 rounded-xl border-2 focus:ring-0",
                            detailsErrors.location
                              ? "border-red-500 focus:border-red-500"
                              : "border-slate-200 focus:border-[#4669ed]"
                          )}
                        />
                        {detailsErrors.location && (
                          <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                            <AlertCircle className="h-3 w-3" />
                            {detailsErrors.location}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                          <Briefcase className="inline h-4 w-4 mr-1" />
                          Industry*
                        </label>
                        <Input
                          value={tempIndustry}
                          onChange={(e) => {
                            setTempIndustry(e.target.value);
                            if (detailsErrors.industry) {
                              setDetailsErrors(prev => ({ ...prev, industry: undefined }));
                            }
                          }}
                          placeholder="e.g., Healthcare, Finance, E-commerce"
                          className={cn(
                            "h-12 rounded-xl border-2 focus:ring-0",
                            detailsErrors.industry
                              ? "border-red-500 focus:border-red-500"
                              : "border-slate-200 focus:border-[#4669ed]"
                          )}
                        />
                        {detailsErrors.industry && (
                          <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                            <AlertCircle className="h-3 w-3" />
                            {detailsErrors.industry}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                          <Globe className="inline h-4 w-4 mr-1" />
                          Company Website
                        </label>
                        <Input
                          type="text"
                          inputMode="url"
                          value={tempWebsiteUrl}
                          onChange={(e) => {
                            setTempWebsiteUrl(e.target.value);
                            if (detailsErrors.websiteUrl) {
                              setDetailsErrors(prev => ({ ...prev, websiteUrl: undefined }));
                            }
                          }}
                          onBlur={() => {
                            // Validate URL on blur if there's a value
                            if (tempWebsiteUrl.trim() && !isValidUrl(tempWebsiteUrl.trim())) {
                              setDetailsErrors(prev => ({
                                ...prev,
                                websiteUrl: 'Please enter a valid URL (e.g., www.example.com or https://example.com)'
                              }));
                            } else {
                              setDetailsErrors(prev => ({ ...prev, websiteUrl: undefined }));
                            }
                          }}
                          placeholder="www.example.com"
                          className={cn(
                            "h-12 rounded-xl border-2 focus:ring-0",
                            detailsErrors.websiteUrl
                              ? "border-red-500 focus:border-red-500"
                              : "border-slate-200 focus:border-[#4669ed]"
                          )}
                        />
                        {detailsErrors.websiteUrl && (
                          <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                            <AlertCircle className="h-3 w-3" />
                            {detailsErrors.websiteUrl}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="flex gap-2 justify-end">
                      <Button 
                        variant="outline" 
                        onClick={() => {
                          setIsEditingDetails(false);
                          setDetailsErrors({}); // Clear errors on cancel
                        }} 
                        disabled={isSavingDetails}
                        className="rounded-[48px]"
                      >
                        Cancel
                      </Button>
                      <Button 
                        onClick={saveDetails} 
                        disabled={
                          isSavingDetails ||
                          !tempCompanySize ||
                          !tempLocation.trim() ||
                          !tempIndustry.trim() ||
                          (!!tempWebsiteUrl.trim() && !isValidUrl(tempWebsiteUrl.trim()))
                        }
                        className="bg-[#4669ed] hover:bg-[#2b42a3] rounded-[48px] disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isSavingDetails ? (
                          <>
                            <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                            Saving...
                          </>
                        ) : (
                          <>
                            <Check className="h-5 w-5 mr-2" />
                            Save
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 text-sm text-slate-500">
                        <Users className="h-4 w-4" />
                        Company Size
                      </div>
                      <p className="text-slate-900 font-medium">
                        {getCompanySizeLabel(companySize) || <span className="text-slate-400 font-normal">Not specified</span>}
                      </p>
                    </div>

                    <div className="space-y-1">
                      <div className="flex items-center gap-2 text-sm text-slate-500">
                        <MapPin className="h-4 w-4" />
                        Location
                      </div>
                      <p className="text-slate-900 font-medium">
                        {location || <span className="text-slate-400 font-normal">Not specified</span>}
                      </p>
                    </div>

                    <div className="space-y-1">
                      <div className="flex items-center gap-2 text-sm text-slate-500">
                        <Briefcase className="h-4 w-4" />
                        Industry
                      </div>
                      <p className="text-slate-900 font-medium">
                        {industry || <span className="text-slate-400 font-normal">Not specified</span>}
                      </p>
                    </div>

                    <div className="space-y-1">
                      <div className="flex items-center gap-2 text-sm text-slate-500">
                        <Globe className="h-4 w-4" />
                        Website
                      </div>
                      {websiteUrl ? (
                        <a
                          href={websiteUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[#4669ed] font-medium hover:underline flex items-center gap-1"
                        >
                          {websiteUrl.replace(/^https?:\/\//, '')}
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      ) : (
                        <p className="text-slate-400 font-normal">Not specified</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </SlideIn>
          </div>

          {/* Completion Sidebar */}
          <SlideIn direction="right" delay={0.3}>
            <div className="w-full lg:w-[320px] flex-shrink-0 order-1 lg:order-2">
              <div
                className="rounded-2xl sm:rounded-3xl lg:rounded-[32px] border border-slate-200 p-5 sm:p-6 lg:sticky lg:top-8"
                style={{
                  background: 'linear-gradient(158.39deg, rgba(255, 255, 255, 1) 0%, rgba(248, 250, 255, 1) 100%)',
                }}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      completion.isComplete ? 'bg-emerald-500' : 'bg-amber-500'
                    }`}
                  >
                    {completion.isComplete ? (
                      <Check className="w-5 h-5 text-white" />
                    ) : (
                      <AlertCircle className="w-5 h-5 text-white" />
                    )}
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-slate-900">Profile Completion</h3>
                    <p className="text-sm text-slate-500">{completion.completedCount} of {completion.totalCount} completed</p>
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="flex items-center gap-3 mb-6">
                  <div className="flex-1 bg-slate-100 rounded-full h-2.5 overflow-hidden">
                    <div
                      className={`h-full transition-all duration-500 ${
                        completion.isComplete ? 'bg-emerald-500' : 'bg-amber-500'
                      }`}
                      style={{ width: `${completion.percentage}%` }}
                    />
                  </div>
                  <span className="text-lg font-bold text-slate-900">{completion.percentage}%</span>
                </div>

                {/* Checklist */}
                <div className="space-y-2">
                  {completion.items.map((item) => (
                    <div
                      key={item.id}
                      className={cn(
                        "flex items-center gap-3 p-3 rounded-xl transition-colors",
                        item.completed ? 'bg-slate-50' : 'bg-amber-50 cursor-pointer hover:bg-amber-100'
                      )}
                      onClick={() => {
                        if (!item.completed) {
                          // Trigger edit mode for the relevant section based on item ID
                          if (item.id === 'basic') {
                            setTempCompanyName(companyName);
                            setTempEmail(email);
                            setIsEditingBasicInfo(true);
                          } else if (item.id === 'about') {
                            setTempBio(bio);
                            setIsEditingBio(true);
                          } else if (item.id === 'details') {
                            setTempCompanySize(companySize);
                            setTempIndustry(industry);
                            setTempLocation(location);
                            setTempWebsiteUrl(websiteUrl);
                            setIsEditingDetails(true);
                          }
                        }
                      }}
                    >
                      <div
                        className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
                          item.completed
                            ? 'bg-emerald-500'
                            : 'bg-white border-2 border-amber-400'
                        }`}
                      >
                        {item.completed && <Check className="h-3 w-3 text-white" />}
                      </div>
                      <span
                        className={`text-sm font-medium ${
                          item.completed ? 'text-slate-400 line-through' : 'text-slate-900'
                        }`}
                      >
                        {item.label}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </SlideIn>
        </div>
      </div>
    </div>
  );
}
